import React, { useEffect, useMemo, useState } from 'react';
import { count, getAggregateFromServer, getDocs, orderBy, query, sum, where } from 'firebase/firestore';
import { typeOfOrder, paymentPerson, paymentMethodd, packageTypeLabels } from '../../../../utils/index';
import { Stack, Button, TextField, MenuItem } from '@mui/material';
import { Title } from '../../../../components/Title/Title';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import { Header } from '../../../../components/Header/Header';
import { useNavigate } from 'react-router-dom';
import { citiesRef, couriersRef, returnedOrderRef } from '../../../../utils/collectionRefs';
import OrderSort from '../../../../components/OrderSort';
import { fieldsToSearchOrder, headersForCompletedOrders, returnedOrderAlternativeStatusLabels, returnedOrderStatus, returnedOrderStatusLabels } from './constants';
import AccessTimeFilledRoundedIcon from '@mui/icons-material/AccessTimeFilledRounded';
import FileCopyRoundedIcon from '@mui/icons-material/FileCopyRounded';
import OrderGrid from '../../../../components/OrderGrid ';
import './scss/Orders.scss';
import SearchInput from '../../../../components/SearchInput/SearchInput';
import { Helmet } from 'react-helmet';
import { orderColumns } from './helpers/orderColumns';
import { handleCopyOrder } from './helpers/handleCopyOrder';
import OrderStatistics from '../../../../components/OrderStatistics';
import { handleChangeStatus } from './helpers/handleChangeStatus';
import { dateFormatter } from '../../../../utils/dateFormatter';
import * as XLSX from 'xlsx';
import { getCourierName } from './helpers/getCourierName';
import { Controller, useForm } from 'react-hook-form';

const ExportToExcelButtons = ({ ordersData }) => {
  const exportToExcel = () => {
    const data = ordersData.map((order) => [
      dateFormatter(order.dateCreated?.seconds),
      dateFormatter(order.dateDelivered?.seconds),
      !order.pid ? 'Отсутствует' : order.pid,
      !order.courierOne ? 'Отсутствует' : order.courierOne,
      !order.courierTwo ? 'Отсутствует' : order.courierTwo,
      returnedOrderStatusLabels[order.status],
      packageTypeLabels[order.packageType],

      order.addressFrom?.cityName || null,
      `${order.senderName}, (${order.sender})`,

      order.addressTo?.cityName || null,
      `${order.receiverName},(${order.receiverPhone})`,

      !order.cost ? `${order.tariff?.cost}` : `${order.cost}`,
      `${order?.redemption}`,
      (() => {
        switch (order?.whoPays) {
          case 1:
            return 'Отправитель';
          case 2:
            return 'Получатель';
          default:
            return 'Совместно';
        }
      })(),
      `${order?.senderPays === null ? '0.0' : order?.senderPays}`,
    ]);

    const finalData = [headersForCompletedOrders, ...data];
    const ws = XLSX.utils.aoa_to_sheet(finalData);
    ws['!cols'] = Array(finalData[0].length).fill({ wch: 22 });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Список возвратных заказов');

    XLSX.writeFile(wb, 'Список возвратных заказов.xlsx');
  };

  return (
    <Button variant='black-contained' onClick={exportToExcel}>
      Экспорт в Excel
    </Button>
  );
};


const ReturnedOrders = ({ pageTitle }) => {
  const [orders, setOrders] = useState(null);
  const [couriers, setCouriers] = useState(null);

  const currentDate = new Date();
  const sevenDaysAgo = new Date(currentDate);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 10);

  const formattedCurrentDate = currentDate.toISOString().substr(0, 10);
  const formattedSevenDaysAgo = sevenDaysAgo.toISOString().substr(0, 10);
  const [cities, setCities] = useState(null);
  const [search, setSearch] = useState('');
  const [aggregates, setAggregates] = useState({})

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      cityTo: '',
      cityFrom: '',
      packageType: '',
      whoPays: '',
      paymentMethod: '',
      status: '',
      courierOne: '',
      selectedDate: formattedSevenDaysAgo,
      selectedDate2: formattedCurrentDate,
    },
  });


  const navigate = useNavigate();
  const clearFilter = () => {
    reset({
      cityTo: '',
      cityFrom: '',
      packageType: '',
      whoPays: '',
      paymentMethod: '',
      status: '',
      courierOne: '',
      selectedDate: formattedSevenDaysAgo,
      selectedDate2: formattedCurrentDate,
    });
    setOrders([]);
  };

  const onSubmit = async (data) => {
    console.log(data)
    try {
      let q = returnedOrderRef;

      Object.entries(data).forEach(([field, value]) => {
        if (
          value !== '' &&
          value !== 'Все' &&
          field !== 'selectedDate' &&
          field !== 'selectedDate2'
        ) {
          q = query(q, where(field, '==', value));
        }
      });

      if (data.selectedDate && data.selectedDate2) {
        const start = new Date(data.selectedDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(data.selectedDate2);
        end.setHours(23, 59, 59, 999);

        q = query(q, where('dateCreated', '>=', start), where('dateCreated', '<=', end));
      }

      q = query(q, orderBy('dateCreated', 'desc'));

      const snapshot = await getAggregateFromServer(q, {
        count: count(),
        totalCost: sum('cost'),
        totalRedemption: sum('redemption'),
      });
      setAggregates({ ...snapshot.data() });

      const querySnapshot = await getDocs(q);
      const newOrders = querySnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setOrders(newOrders);
    } catch (error) {
      console.error('Ошибка фильтрации:', error);
    }
  };

  useEffect(() => {
    fetchData(citiesRef, 'name', 'asc', setCities);
    fetchData(couriersRef, 'name', 'asc', setCouriers);
  }, []);

  const fetchData = async (collectionRef, sortField, sortOrder, setData) => {
    try {
      const querySnapshot = await getDocs(
        query(collectionRef, orderBy(sortField, sortOrder))
      );
      const data = querySnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setData(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  useEffect(() => {
    if (!couriers?.length || !orders) return;


    setOrders((prevOrders) => {
      const updatedOrders = prevOrders.map((order) => {
        const courierOneInfo = getCourierName(order.courierOne, couriers);
        const courierTwoInfo = getCourierName(order.courierTwo, couriers);

        return {
          ...order,
          courierOneName: courierOneInfo.name,
          courierTwoName: courierTwoInfo.name,
        };
      });

      if (JSON.stringify(prevOrders) === JSON.stringify(updatedOrders)) {
        return prevOrders;
      }
      return updatedOrders;
    });
  }, [couriers, orders]);


  const handleStatusChange = async (id, newStatus) => {
    await handleChangeStatus({
      orders: orders,
      id,
      newStatus,
      dataRef: returnedOrderRef,
      returnedOrderStatusLabels,
      returnedOrderAlternativeStatusLabels,
    });
  };

  const handleSearchOrder = (e) => {
    setSearch(e.target.value.toLowerCase().trim());
  };

  const filteredData = useMemo(() => {
    if (!orders) return { filteredOrders: [], aggregates: { count: 0, totalCost: 0, totalRedemption: 0 } };
    const filteredOrders = orders.filter((order) =>
      fieldsToSearchOrder.some((field) => {
        const value = order[field];
        return value && value.toLowerCase().includes(search.toLowerCase());
      })
    );

    const aggregates = {
      count: filteredOrders.length,
      totalCost: filteredOrders.reduce((sum, order) => sum + (order.cost || 0), 0),
      totalRedemption: filteredOrders.reduce((sum, order) => sum + (order.redemption || 0), 0),
    };

    return { filteredOrders, aggregates };
  }, [orders, search]);

  const actionsConfig = [
    {
      title: 'Копировать',
      icon: <FileCopyRoundedIcon style={{ color: '#000b1f' }} />,
      handler: handleCopyOrder,
    },
    {
      title: 'История',
      icon: <AccessTimeFilledRoundedIcon style={{ color: '#000b1f' }} />,
      handler: (params) => navigate(`/order/history/orders/${params.id}`),
    },
  ];

  const columns = orderColumns({
    actionsConfig: actionsConfig,
    path: '/order/returned/details',
    editable: true,
    customLabels: returnedOrderStatusLabels,
    orderType: 'completed',
  });

  return (
    <>
      <div className='containerr'>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <Header previous='Статистика' initial='Возвраты' />
        <Title
          title='Возвраты'
          icon={<AssignmentTurnedInRoundedIcon fontSize='24' />}
        />
        <div className='container-inner'>
          <div className='order-statistics-container'>
            <OrderStatistics aggregates={search ? filteredData.aggregates : aggregates} />
          </div>

          <div className='orders-sorting-header'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Button variant='black-contained' onClick={clearFilter} sx={{ mr: '10px' }}>
                Очистить
              </Button>
              <Button type="submit" variant="black-contained">Применить</Button>
              <Stack
                sx={{ width: '100%', flexWrap: 'wrap' }}
                direction='row'
                gap={2}
              >
                <Controller
                  name="packageType"
                  control={control}
                  render={({ field }) => (
                    <OrderSort
                      sortKey="По типу"
                      options={typeOfOrder}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="whoPays"
                  control={control}
                  render={({ field }) => (
                    <OrderSort
                      sortKey="Кто оплачивает"
                      options={paymentPerson}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="paymentMethod"
                  control={control}
                  render={({ field }) => (
                    <OrderSort
                      sortKey="Метод оплаты"
                      options={paymentMethodd}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <OrderSort
                      sortKey="По статусу"
                      options={returnedOrderStatus}
                      {...field}
                    />
                  )}
                />

                <div className='order-sort'>
                  <Controller
                    name="cityFrom"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        select
                        label="Откуда"
                        fullWidth
                        size="small"
                        {...field}
                      >
                        <MenuItem value="">Все</MenuItem>
                        {cities?.map((option) => (
                          <MenuItem key={option.name} value={parseInt(option.id)}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </div>
                <div className='order-sort'>
                  <Controller
                    name="cityTo"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        select
                        label="Куда"
                        fullWidth
                        size="small"
                        {...field}
                      >
                        <MenuItem value="">Все</MenuItem>
                        {cities?.map((option) => (
                          <MenuItem key={option.name} value={parseInt(option.id)}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </div>
                <div className='order-sort'>
                  <Controller
                    name="courierOne"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        select
                        label="Курьер"
                        fullWidth
                        size="small"
                        {...field}
                      >
                        <MenuItem value="">Все</MenuItem>
                        {couriers?.map((option) => (
                          <MenuItem key={option.name} value={option.phone}>
                            {option.name} ({option.phone})
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </div>
                <div className='order-sort order-sort-date'>
                  <label htmlFor='orderDate' className='orders-sort-date-label'>
                    От
                    <Controller
                      name="selectedDate"
                      control={control}
                      render={({ field }) => (
                        <input
                          className='order-sort-dateInput'
                          type='date'
                          name='orderDate1'
                          id='orderDate1'
                          {...field}
                        />
                      )}
                    />

                  </label>
                  <span></span>
                  <label htmlFor='orderDate2' className='orders-sort-date-label'>
                    До
                    <Controller
                      name="selectedDate2"
                      control={control}
                      render={({ field }) => (
                        <input
                          className='order-sort-dateInput'
                          type='date'
                          name='orderDate2'
                          id='orderDate2'
                          {...field}
                        />
                      )}
                    />
                  </label>
                </div>
              </Stack>
            </form>

          </div>
          <div className='orders-card-wrapper'>
            <div className='orders-card-header'>
              <div className='orders-card-parent'>
                <div className='orders-card-parent__btns'>
                  <ExportToExcelButtons ordersData={orders} />
                </div>
                <div style={{ width: '41%' }}>
                  <SearchInput
                    label='Поиск'
                    placeholder='Введите'
                    variant='outlined'
                    onChange={handleSearchOrder}
                  />
                </div>
              </div>
            </div>
            <OrderGrid
              orders={filteredData.filteredOrders || orders}
              columns={columns}
              size={20}
              handleChangeStatus={handleStatusChange}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ReturnedOrders;
